import React from "react";
import EstatePage from "@/components/sites/EstatePage";
import Reveal from "@/components/sites/Reveal";
import { useBrand } from "@/lib/brand";

/**
 * The fees page, in the Estate theme.
 *
 * An agency's own published fee structure, from `brand.fees`. A page with
 * nothing behind it says so rather than showing an empty table: an estate
 * agent's fees page that appears to list nothing reads as an agency with
 * something to hide, which is the opposite of why it exists.
 */
export default function EstateFees({ preview = true }) {
  const brand = useBrand();
  const biz = brand.business || {};
  const fees = brand.fees || {};
  const plans = fees.plans || [];

  return (
    <EstatePage
      preview={preview}
      title={biz.name ? `Fees, ${biz.name}` : undefined}
      description={fees.intro || undefined}
      eyebrow="What we charge"
      heading="Fees"
      lede={fees.intro || ""}
      crumbs={[{ label: "Fees" }]}
    >
      {plans.length > 0 ? (
        <Reveal>
          {/* data-cols, not an inline style: an inline style beats the
              phone media query, and a two-option agency got two columns
              at 375px with the price painting off the screen. */}
          <div className="fees" data-cols={plans.length < 3 ? plans.length : undefined}>
            {plans.map((plan) => (
              <div key={plan.name} className={`fee${plan.featured ? " lead" : ""}`}>
                {plan.area && <div className="tagline">{plan.area}</div>}
                <h2>{plan.name}</h2>
                {plan.price && <div className="amount">{plan.price}</div>}
                {plan.price_note && (
                  <div className="note" style={{ marginTop: 6 }}>{plan.price_note}</div>
                )}
                {plan.subtitle && <p className="detail">{plan.subtitle}</p>}
                {plan.description && (
                  <p className="detail" style={{ color: "var(--muted)" }}>{plan.description}</p>
                )}
                {plan.features?.length > 0 && (
                  <ul>{plan.features.map((f) => <li key={f}>{f}</li>)}</ul>
                )}
                {plan.you_manage?.length > 0 && (
                  <div style={{ marginTop: 20, paddingTop: 18, borderTop: "1px solid var(--line)" }}>
                    <div className="tagline" style={{ color: "var(--muted)" }}>You manage</div>
                    <ul style={{ marginTop: 10 }}>
                      {plan.you_manage.map((m) => <li key={m}>{m}</li>)}
                    </ul>
                  </div>
                )}
                {plan.best_suited && (
                  <p className="note" style={{ marginTop: 18, paddingTop: 16,
                                              borderTop: "1px solid var(--line)" }}>
                    <b style={{ color: "var(--ink)" }}>Best suited to: </b>{plan.best_suited}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Reveal>
      ) : (
        <p className="lede">
          Our fees depend on the property and how you want it marketed.
          {biz.phone ? " Call us and we will tell you exactly what it would cost." : ""}
        </p>
      )}

      {fees.notes?.length > 0 && (
        <Reveal>
          <ul className="reasons" style={{ marginTop: 48 }}>
            {fees.notes.map((note) => <li key={note}>{note}</li>)}
          </ul>
        </Reveal>
      )}
    </EstatePage>
  );
}
